import React, { useContext, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { Hide, Show } from '@atlaskit/primitives/responsive';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import type { EditorActions } from '@atlaskit/editor-core';
import Tooltip from '@atlaskit/tooltip';

import { EditorBpqaSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorBpqaSpotlight.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	CONTEXT_PANEL_TAB_TARGET_TEMPLATES,
	isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen,
	isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpenWithTemplatesPanelMode,
	removeInEditorTemplatesQueryParam,
	setInEditorTemplatesPanelQueryParam,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { TEMPLATE_PREVIEW_DELAY, TEMPLATE_SKELETONS } from './bpqaConst.ts';
import { i18n } from './bpqai18n.ts';
import type { QuickTemplateType } from './QuickTemplateCard.tsx';
import { QuickTemplateCard } from './QuickTemplateCard.tsx';
import { AllTemplatesLink } from './AllTemplatesLink.tsx';
import { useBlankPageQuickActionsContext } from './BlankPageQuickActionsContext.tsx';

const listStyles = cssMap({
	ul: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- needed to override wiki overrides for list styles
		listStyle: 'none !important',
		margin: token('space.0'),
		padding: token('space.0'),
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.200'),
	},
});

const errorMessageStyle = xcss({
	color: 'color.text.subtlest',
	fontWeight: token('font.weight.regular'),
});

export interface QuickTemplateRowProps {
	cancelDebouncedSaveDraft: () => void;
	contentId: string;
	disabled: boolean;
	showSpinner: () => void;
	hideSpinner: () => void;
	spaceKey: string;
	title: string | null;
	flags: FlagsStateContainer;
	editorActions: EditorActions;
	onTitleChange: (value: string) => void;
}

const QuickTemplateRowComponent = ({
	cancelDebouncedSaveDraft,
	contentId,
	disabled,
	showSpinner,
	hideSpinner,
	spaceKey = '',
	title,
	flags,
	editorActions,
	onTitleChange,
}: QuickTemplateRowProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { templates, error, loading, setTemplateIndex, setButtonHovered, revampedBpqa } =
		useBlankPageQuickActionsContext();

	const handleMouseEnter = () => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
		}
		timeoutIdRef.current = setTimeout(() => {
			setButtonHovered(true);
		}, TEMPLATE_PREVIEW_DELAY);
	};

	const handleMouseLeave = () => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
		}
		timeoutIdRef.current = setTimeout(() => {
			setButtonHovered(false);
		}, TEMPLATE_PREVIEW_DELAY);
	};

	const isPanelOpen = isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen(getQueryParams);
	const isPanelOpenWithTemplatesTabTarget =
		isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpenWithTemplatesPanelMode(getQueryParams);

	// Only active when panel is open AND in templates mode (not import mode)
	const isAllTemplatesLinkActive = expVal('cc_snippets', 'isEnabled', false)
		? isPanelOpenWithTemplatesTabTarget
		: isPanelOpen;

	const handleAllTemplatesClick = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'bpqaAllTemplatesLink',
				attributes: {
					version: '2',
					revampedBpqa,
				},
			},
		}).fire();

		if (isAllTemplatesLinkActive) {
			removeInEditorTemplatesQueryParam(setQueryParams);
		} else {
			setInEditorTemplatesPanelQueryParam(
				setQueryParams,
				IN_EDITOR_TEMPLATES_QUERY_OPEN,
				CONTEXT_PANEL_TAB_TARGET_TEMPLATES,
			);
		}
	};

	if (error) {
		return (
			<Inline
				alignInline="start"
				alignBlock="center"
				xcss={errorMessageStyle}
				shouldWrap
				space="space.100"
				testId="blank-page-quick-actions-all-templates-error"
			>
				<StatusErrorIcon color={token('color.text.danger')} label="" />
				<FormattedMessage
					{...i18n.templateLoadError}
					values={{
						button: (chunks: React.ReactNode[]) => (
							<Button
								appearance="link"
								testId="blank-page-quick-actions-all-templates-error-link"
								spacing="none"
								onClick={handleAllTemplatesClick}
								isDisabled={disabled}
							>
								{chunks}
							</Button>
						),
					}}
				/>
			</Inline>
		);
	}

	if (loading || (!loading && !templates.length)) {
		return (
			<Inline space="space.200" alignInline="start" alignBlock="center" shouldWrap>
				{new Array(TEMPLATE_SKELETONS).fill(null).map((_, idx) => (
					<Skeleton
						key={`skeleton-${idx}`}
						width="115px"
						height="32px"
						isShimmering
						testId="bpqa-template-button-skeleton"
					/>
				))}
				<AllTemplatesLink
					isAllTemplatesLinkActive={isAllTemplatesLinkActive}
					disabled={disabled}
					handleAllTemplatesClick={handleAllTemplatesClick}
				/>
			</Inline>
		);
	}

	const bpqaFlagId = 'bpqa-success-flag';

	const handleClearEditor = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'clearEditor',
				attributes: {
					version: '2',
					revampedBpqa,
				},
			},
		}).fire();
		void flags.hideFlag(bpqaFlagId);
		editorActions?.clear();
		onTitleChange('');
	};

	const handleBrowseOtherTemplates = () => {
		setInEditorTemplatesPanelQueryParam(
			setQueryParams,
			IN_EDITOR_TEMPLATES_QUERY_OPEN,
			CONTEXT_PANEL_TAB_TARGET_TEMPLATES,
		);
		void flags.hideFlag(bpqaFlagId);
	};

	const onSuccess = ({ templateName }: { templateName: string }): void => {
		const browseOtherTemplates = {
			content: <FormattedMessage {...i18n.templateSuccessFlagBrowserOtherTemplatesLink} />,
			onClick: handleBrowseOtherTemplates,
		};

		const clearEditor = {
			content: <FormattedMessage {...i18n.templateSuccessFlagClearEditorLink} />,
			onClick: handleClearEditor,
		};

		const actions = [browseOtherTemplates, clearEditor];

		void flags.showFlag({
			id: bpqaFlagId,
			type: 'success-circle',
			title: (
				<FormattedMessage
					{...i18n.templateSuccessFlagTitle}
					values={{
						templateName,
					}}
				/>
			),
			description: <FormattedMessage {...i18n.templateSuccessFlagDescription} />,
			actions,
			messageType: 'transactional',
			messageId: 'bpqa-template-success-flag',
		});
	};

	return (
		<Inline
			space="space.200"
			alignInline="start"
			alignBlock="center"
			shouldWrap
			testId="bpqa-quick-template-row"
		>
			{revampedBpqa ? (
				<EditorBpqaSpotlight>
					<Hide below="lg">
						<FormattedMessage {...i18n.revampedBpqaTemplatePrefix} />
					</Hide>
				</EditorBpqaSpotlight>
			) : null}
			<Box as="ul" role="list" xcss={listStyles.ul}>
				{templates.map((template: QuickTemplateType, idx: number) => {
					const templateKey = template.contentBlueprintId || template.templateId;
					// hide third card below 768px and hide all cards below 480px
					const displayThreshold = idx === templates.length - 1 ? 'sm' : 'xs';

					return (
						<Show above={displayThreshold} key={templateKey}>
							<Box as="li">
								<Tooltip position="top" content={template.name}>
									<QuickTemplateCard
										template={template}
										disabled={disabled}
										spaceKey={spaceKey}
										createAnalyticsEvent={createAnalyticsEvent}
										index={idx}
										key={templateKey}
										contentId={contentId}
										title={title}
										cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
										showSpinner={showSpinner}
										hideSpinner={hideSpinner}
										onSuccess={onSuccess}
										setTemplateIndex={setTemplateIndex}
										handleMouseEnter={handleMouseEnter}
										handleMouseLeave={handleMouseLeave}
									/>
								</Tooltip>
							</Box>
						</Show>
					);
				})}
			</Box>
			<AllTemplatesLink
				isAllTemplatesLinkActive={isAllTemplatesLinkActive}
				disabled={disabled}
				handleAllTemplatesClick={handleAllTemplatesClick}
			/>
		</Inline>
	);
};

export const QuickTemplateRow = withFlags(QuickTemplateRowComponent);
